import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import {
  CalendarHeart,
  CalendarCheck,
  CalendarPlus,
  Plus,
} from 'lucide-react';
import { startOfDay, endOfDay } from 'date-fns';
import type { CustomerDetailData, ReservationType } from '~/types/customer-detail.types';
import clsx from 'clsx';
import { Constants } from '~/utils/constants';
import { DatePickerSheet } from '@sakura-grp/ui/date-picker-sheet';
import { useImeSafeSearch } from '@/hooks/useImeSafeSearch';
import { useResolvedCustomerCompany } from '@/hooks/useResolvedCustomerCompany';

interface ReserveTabContentProps {
  data: CustomerDetailData;
}

export const ReserveTabContent = ({ data }: ReserveTabContentProps) => {
  // 仕様: docs/domain/20260527-customer-company-context-display.md §営業履歴
  // 閲覧者視点 (ログイン中の自分) で代表名刺の会社を解決して、全行で表示
  const resolvedCompanyName = useResolvedCustomerCompany(data.id);

  const [appliedSearchText, setAppliedSearchText] = useState('');
  const search = useImeSafeSearch({onApply: setAppliedSearchText});
  const [showPreReservation, setShowPreReservation] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(
    () => new Set(data.stores.map(s => s.storeId))
  );

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) {
        next.delete(storeId);
      } else {
        next.add(storeId);
      }
      return next;
    });
  };

  const isDateActive = !!selectedDate;

  // フィルタリングされた予約リスト
  const filteredReservations = useMemo(() => {
    return data.reservations.filter(reservation => {
      // 予約前フィルター
      if (showPreReservation && reservation.isPast) return false;

      // 日付フィルター（カレンダー選択）
      if (selectedDate) {
        const datePart = reservation.date; // yyyy.MM.dd format
        const [year, month, day] = datePart.split('.').map(Number);
        const reservationDate = new Date(year, month - 1, day);
        const from = startOfDay(selectedDate);
        const to = endOfDay(selectedDate);
        if (reservationDate < from || reservationDate > to) return false;
      }

      // テキスト検索（店舗名）
      if (appliedSearchText) {
        const q = appliedSearchText.toLowerCase();
        const match = reservation.store.toLowerCase().includes(q);
        if (!match) return false;
      }

      // 店舗フィルター
      if (selectedStoreIds.size < data.stores.length) {
        const storeMatch = data.stores.some(
          s => selectedStoreIds.has(s.storeId) &&
            (reservation.store === s.storeName || reservation.store === s.storeShortName)
        );
        if (!storeMatch) return false;
      }
      return true;
    });
  }, [data.reservations, data.stores, appliedSearchText, showPreReservation, selectedDate, selectedStoreIds]);

  const getReservationIcon = (type: ReservationType) => {
    const iconColor = 'text-brand';
    const iconClass = `translate-y-xs size-[2.4rem] flex-none ${iconColor}`;
    const strokeWidth = 1.5;

    switch (type) {
      case '同伴':
        return <CalendarHeart strokeWidth={strokeWidth} className={iconClass} />;
      case '確約':
        return <CalendarCheck strokeWidth={strokeWidth} className={iconClass} />;
      case '予約':
        return <CalendarPlus strokeWidth={strokeWidth} className={iconClass} />;
    }
  };

  return (
    <div className="border-t">
      {/* ツールバー */}
      <div className="px-lg gap-md h-(--h-list) flex items-center border-b">
        {/* 予約前フィルター */}
        <label className="space-y-sm grid cursor-pointer place-items-center leading-none">
          <input
            type="checkbox"
            checked={showPreReservation}
            onChange={(e) => setShowPreReservation(e.target.checked)}
            className="peer sr-only"
          />
          <div className="peer relative h-6 w-11 rounded-full border-gray-500 bg-gray-400 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand"></div>
          <p className="text-2xs -mx-sm text-center font-bold tracking-tight text-stone-400 peer-checked:text-brand">
            予約前
          </p>
        </label>

        {/* 検索フィールド */}
        <input
          data-slot="input"
          className="h-xl bg-input/10 text-md shadow-xs selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground/50 focus-visible:border-ring focus-visible:ring-ring/50 flex min-w-0 flex-1 rounded-lg border px-2 py-1 outline-none transition-[color,box-shadow] focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          placeholder="名前・会社名で検索"
          {...search.inputProps}
        />

        {/* カレンダーボタン */}
        <button
          type="button"
          className={`h-xl p-md shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex flex-none items-center justify-center rounded-md border outline-none transition-all focus-visible:ring-[3px] ${isDateActive ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-background'}`}
          onClick={() => setCalendarOpen(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-calendar size-4"
            aria-hidden="true"
          >
            <path d="M8 2v4"></path>
            <path d="M16 2v4"></path>
            <rect width="18" height="18" x="3" y="4" rx="2"></rect>
            <path d="M3 10h18"></path>
          </svg>
        </button>
        <DatePickerSheet
          open={calendarOpen}
          onOpenChange={setCalendarOpen}
          value={selectedDate}
          onChange={setSelectedDate}
          title="予約日を選択"
        />

        {/* 店舗フィルター */}
        <div className="!divide-(--border) flex flex-none items-center divide-x rounded-lg border bg-white text-xs font-bold">
          {data.stores.map((store, index) => {
            const isChecked = selectedStoreIds.has(store.storeId);
            return (
              <div key={store.storeShortName} className="gap-xs px-md flex items-center justify-center">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isChecked}
                  data-state={isChecked ? 'checked' : 'unchecked'}
                  className="size-(--bigger-sm) border-input shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground peer rounded-sm border bg-white outline-none transition-shadow focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                  id={`reserve-store-${index + 1}`}
                  onClick={() => toggleStore(store.storeId)}
                >
                  {isChecked && (
                    <span className="flex items-center justify-center text-current transition-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-check size-md"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                    </span>
                  )}
                </button>
                <label
                  className="h-xl gap-md flex cursor-pointer select-none items-center text-sm font-bold leading-none tracking-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                  htmlFor={`reserve-store-${index + 1}`}
                >
                  {store.storeShortName}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* 予約履歴リスト */}
      <ul className="divide-y divide-stone-200 border-b">
        {filteredReservations.length > 0 ? (
          filteredReservations.map((reservation) => (
            <li
              key={reservation.id}
              className={clsx([
                'px-lg gap-md h-(--h-list) relative flex items-center leading-none',
                reservation.isPast && 'opacity-50',
              ])}
            >
              {/* 店舗名（右上） */}
              <strong className="py-sm bg-secondary text-primary text-2xs absolute end-0 top-0 rounded-bl-lg border-b border-s px-[.7em] font-bold leading-snug tracking-tight">
                {reservation.store}
              </strong>

              {/* 予約タイプアイコン */}
              <div className="text-md space-y-sm text-center tracking-normal text-brand">
                {getReservationIcon(reservation.type)}
                <span className="block font-bold">{reservation.type}</span>
              </div>

              {/* 日時 */}
              <time
                className={clsx([
                  'flex-none items-center text-center text-lg font-bold tracking-tight',
                  reservation.isPast && 'line-through',
                ])}
              >
                <small className="block">
                  {reservation.date}
                  <span className="text-(length:--smaller-md) ms-xs">({reservation.dayOfWeek})</span>
                </small>
                <small className="text-(length:--bigger-lg) block leading-none">
                  {reservation.time} <span className="text-(length:--smaller-lg)">~</span>
                </small>
              </time>

              {/* 顧客情報 */}
              <Link to={`/customers/${data.id}`} className="space-y-sm">
                <div className="gap-sm flex items-center">
                  <div className="float-none w-[2.45rem] text-center text-[2.45rem]">{data.icon}</div>
                  <div className="space-y-xs leading-none">
                    <h3 className="text-xl">
                      {data.name}
                      <small className="ms-xs">({reservation.guestCount})</small>
                    </h3>
                    <small className="mx-xs text-(length:--smaller-xl) line-clamp-1 tracking-normal">
                      {resolvedCompanyName && `( ${resolvedCompanyName} )`}
                    </small>
                  </div>
                </div>
              </Link>

              {/* プラスボタン */}
              <button
                className={clsx([
                  'size-lg gap-sm ms-auto flex flex-none items-center justify-center rounded-md text-xs leading-none tracking-tight',
                  reservation.isPast && 'invisible hidden',
                  Constants.btnWhite,
                ])}
              >
                <Plus className="size-md" />
              </button>
            </li>
          ))
        ) : (
          <li className="px-lg py-xl text-center text-sm text-gray-400">予約履歴がありません</li>
        )}
      </ul>
    </div>
  );
};
